import type { ReactNode } from 'react';
import {
  BadgePercent,
  CalendarClock,
  FileStack,
  Gift,
  Handshake,
  Layers,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import {
  capturePointStationPricing,
  liteDiscountPct,
  type CapturePointCustomerDiscount,
  type CheckoutPlan,
} from '../shared/engagement';
import { pagePackListPriceUsd, pagePackPages } from '../shared/v6-page-allowance';
import lite from './assets/plans/lite.webp';
import custom from './assets/plans/custom.webp';
import './plan-page.css';

/**
 * "Choose your plan" (docs/planning/lite-tier.md), modelled on Paige v2's pricing page: the plans
 * as a list on the left, the one picked in a spotlight on the right with its engraving, price, four
 * facts and one button. The engravings are Paige's (filing cabinets for Lite, the archive for
 * Enterprise). The button is the caller's: Buy now opens the existing card checkout, Enterprise the
 * existing inquiry. A partner's discount shows the price less it, the list price struck.
 */
export type PlanKey = 'lite' | 'custom';

/** Lite's pages: 5,000 a month, or 60,000 a year up front. */
export const litePages = { monthly: 5_000, yearly: 60_000 } as const;

/** Dollars less a whole percent, to the cent, as Stripe takes it. */
export const afterDiscount = (dollars: number, percent: number): number =>
  percent <= 0 ? dollars : Math.round((Math.round(dollars * 100) * (100 - percent)) / 100) / 100;

/** Lite's list and discounted price for one interval, and what a year saves against twelve months. */
export const litePrice = (plan: CheckoutPlan, discount: CapturePointCustomerDiscount | null) => {
  const pct = liteDiscountPct(discount, plan);
  const list =
    plan === 'yearly'
      ? capturePointStationPricing.yearlyUsd
      : capturePointStationPricing.monthlyUsd;
  const due = afterDiscount(list, pct);
  const month = afterDiscount(
    capturePointStationPricing.monthlyUsd,
    liteDiscountPct(discount, 'monthly'),
  );
  const saving = plan === 'yearly' ? Math.max(0, Math.round((month * 12 - due) * 100) / 100) : 0;
  return { list, due, pct, saving };
};

/** "$1,900", "$171.50": cents only when there are some. */
export const formatPrice = (dollars: number): string =>
  `$${dollars.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2, maximumFractionDigits: 2 })}`;

export const discountLine = (percent: number): string => `Your partner's discount: ${percent}% off`;

const art: Record<PlanKey, string> = { lite, custom };
const names: Record<PlanKey, string> = { lite: 'Lite', custom: 'Enterprise' };
const blurbs: Record<PlanKey, string> = {
  lite: 'Every feature, paid by the page.',
  custom: 'Ademero builds a plan around your volume.',
};

export interface PlanPageProps {
  interval: CheckoutPlan;
  onInterval: (plan: CheckoutPlan) => void;
  selected: PlanKey;
  onSelect: (plan: PlanKey) => void;
  /** The plan this computer is on (from its page snapshot), marked "Your plan"; null on a trial. */
  current: { plan: PlanKey; interval: CheckoutPlan | null } | null;
  /** The trial in the header pill ("12 days left in your trial"); null when not on a trial. */
  trial: string | null;
  /** The partner's customer discount from the partner-code check; null for none. */
  discount: CapturePointCustomerDiscount | null;
  /** The spotlight's button, wired by the caller. */
  action: ReactNode;
  /** Under the picker, before the fine print (the buyer's details). */
  children?: ReactNode;
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function PlanPage(props: PlanPageProps): ReactNode {
  const { interval, onInterval, selected, onSelect, current, trial, discount, action, children } =
    props;
  const pct = Math.max(liteDiscountPct(discount, 'monthly'), liteDiscountPct(discount, 'yearly'));
  return (
    <section className="plan-page" aria-labelledby="plan-page-title">
      <header className="plan-page__head">
        <h2 id="plan-page-title">{current ? 'Change your plan' : 'Choose your plan'}</h2>
        {trial && (
          <span className="plan-page__pill">
            <CalendarClock size={15} aria-hidden="true" />
            {trial}
          </span>
        )}
        {pct > 0 && (
          <span className="plan-page__pill plan-page__pill--discount">
            <BadgePercent size={15} aria-hidden="true" />
            {discountLine(pct)}
          </span>
        )}
      </header>
      <div className="plan-interval" role="group" aria-label="Billing">
        <button
          type="button"
          data-telemetry="plan-monthly"
          className="plan-interval__option"
          aria-pressed={interval === 'monthly'}
          onClick={() => onInterval('monthly')}
        >
          Monthly
        </button>
        <button
          type="button"
          data-telemetry="plan-yearly"
          className="plan-interval__option"
          aria-pressed={interval === 'yearly'}
          onClick={() => onInterval('yearly')}
        >
          Annual
          <span className="plan-interval__save">
            <Gift size={13} aria-hidden="true" />2 months free
          </span>
        </button>
      </div>
      <div className="plan-picker">
        <div className="plan-picker__list" role="radiogroup" aria-label="Plans">
          {(['lite', 'custom'] as const).map((plan) => (
            <PlanOption
              key={plan}
              plan={plan}
              interval={interval}
              discount={discount}
              on={plan === selected}
              current={
                current?.plan === plan && (plan === 'custom' || current.interval === interval)
              }
              onSelect={() => onSelect(plan)}
            />
          ))}
        </div>
        <article className="plan-spotlight" aria-live="polite">
          <img
            className="plan-spotlight__art"
            src={art[selected]}
            alt=""
            aria-hidden="true"
            decoding="async"
          />
          <div className="plan-spotlight__top">
            <h3>{names[selected]}</h3>
            <p className="plan-spotlight__blurb">{blurbs[selected]}</p>
            <SpotlightPrice plan={selected} interval={interval} discount={discount} />
          </div>
          <ul className="plan-spotlight__facts" aria-label={`${names[selected]} includes`}>
            {factsOf(selected, interval).map((fact) => (
              <Fact key={fact.label} {...fact} />
            ))}
          </ul>
          <div className="plan-spotlight__action">{action}</div>
        </article>
      </div>
      {children}
      <p className="plan-page__fine">
        US dollars, per computer: one license for each station. Annual is 10 months&apos; price for
        12. One page read is one page, however hard; blank pages are free. Extra pages come in packs
        of {pagePackPages.toLocaleString('en-US')} for ${pagePackListPriceUsd}, kept 12 months.
      </p>
    </section>
  );
}

function Was({ list, className }: { list: number | null; className: string }): ReactNode {
  if (list === null) return null;
  return (
    <s className={className}>
      <span className="plan-visually-hidden">was </span>
      {formatPrice(list)}
    </s>
  );
}

function PlanOption({
  plan,
  interval,
  discount,
  on,
  current,
  onSelect,
}: {
  plan: PlanKey;
  interval: CheckoutPlan;
  discount: CapturePointCustomerDiscount | null;
  on: boolean;
  current: boolean;
  onSelect: () => void;
}): ReactNode {
  return (
    <label className={`plan-option${on ? ' on' : ''}${current ? ' current' : ''}`}>
      {/* Fixed literal names (tests/telemetry-tests/no-user-text.cjs): the same names the template built. */}
      <input
        type="radio"
        name="capturepoint-plan"
        data-telemetry={plan === 'custom' ? 'plan-custom' : 'plan-lite'}
        checked={on}
        onChange={onSelect}
      />
      <img
        className="plan-option__art"
        src={art[plan]}
        alt=""
        aria-hidden="true"
        decoding="async"
      />
      <span className="plan-option__name">
        <b>
          {names[plan]}
          {current ? ' ' : null}
          {current ? <span className="plan-option__yours">Your plan</span> : null}
        </b>
        <small>
          {plan === 'custom'
            ? 'More than Lite allows'
            : interval === 'yearly'
              ? `${litePages.yearly.toLocaleString('en-US')} pages a year`
              : `${litePages.monthly.toLocaleString('en-US')} pages a month`}
        </small>
      </span>
      <OptionPrice plan={plan} interval={interval} discount={discount} />
    </label>
  );
}

function OptionPrice({
  plan,
  interval,
  discount,
}: {
  plan: PlanKey;
  interval: CheckoutPlan;
  discount: CapturePointCustomerDiscount | null;
}): ReactNode {
  if (plan === 'custom')
    return <span className="plan-option__price plan-option__price--words">Built for you</span>;
  const price = litePrice(interval, discount);
  if (interval === 'yearly')
    return (
      <span className="plan-option__price plan-option__price--saving">
        <b>Save {formatPrice(price.saving)}</b>
        <small>
          <Was list={price.pct ? price.list : null} className="plan-option__was" />
          {formatPrice(price.due)} / year
        </small>
      </span>
    );
  return (
    <span className="plan-option__price">
      <Was list={price.pct ? price.list : null} className="plan-option__was" />
      {formatPrice(price.due)}
      <small>/ month</small>
    </span>
  );
}

function SpotlightPrice({
  plan,
  interval,
  discount,
}: {
  plan: PlanKey;
  interval: CheckoutPlan;
  discount: CapturePointCustomerDiscount | null;
}): ReactNode {
  if (plan === 'custom')
    return <p className="plan-spotlight__price plan-spotlight__price--words">Built for you</p>;
  const price = litePrice(interval, discount);
  if (interval === 'monthly')
    return (
      <p className="plan-spotlight__price">
        <Was list={price.pct ? price.list : null} className="plan-spotlight__was" />
        {formatPrice(price.due)}
        <small>/ month</small>
      </p>
    );
  return (
    <>
      <p className="plan-spotlight__price plan-spotlight__price--saving">
        Save {formatPrice(price.saving)}
        <small>a year</small>
      </p>
      <p className="plan-spotlight__billed">
        <b>
          <Was list={price.pct ? price.list : null} className="plan-spotlight__was-year" />
          {formatPrice(price.due)} / year
        </b>
        <small>Billed once · 2 months free</small>
      </p>
    </>
  );
}

interface FactTile {
  icon: LucideIcon;
  value: string;
  label: string;
}

function factsOf(plan: PlanKey, interval: CheckoutPlan): FactTile[] {
  if (plan === 'custom')
    return [
      { icon: Layers, value: 'Unlimited', label: 'pages' },
      { icon: Sparkles, value: 'Every feature', label: 'the whole product' },
      { icon: Handshake, value: 'Ademero', label: 'builds your plan' },
      { icon: BadgePercent, value: 'Quote', label: 'priced for your volume' },
    ];
  return [
    interval === 'yearly'
      ? {
          icon: Layers,
          value: litePages.yearly.toLocaleString('en-US'),
          label: 'pages a year, up front',
        }
      : { icon: Layers, value: litePages.monthly.toLocaleString('en-US'), label: 'pages a month' },
    { icon: Sparkles, value: 'Every feature', label: 'the whole product' },
    {
      icon: FileStack,
      value: `$${pagePackListPriceUsd}`,
      label: `per ${pagePackPages.toLocaleString('en-US')} more pages`,
    },
    { icon: RefreshCw, value: interval === 'yearly' ? 'Yearly' : 'Monthly', label: 'pages reset' },
  ];
}

function Fact({ icon: Icon, value, label }: FactTile): ReactNode {
  return (
    <li className="plan-fact">
      <Icon size={20} aria-hidden="true" />
      <span>
        <b>{value}</b>
        <small>{label}</small>
      </span>
    </li>
  );
}
